import { useCallback, useEffect, useRef, useState } from "react";
import { CategoryNodeType } from "src/molecules/CategoryTree";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import {
  generateTree,
  transformCategoryValue,
} from "src/utils/product-listing-helpers";
import {
  useCategoryTreeContext,
  useCategoryTreeDispatch,
} from "../context/use-category-tree-context";
import { PLPType } from "src/types/PLPType.d";
import { CATEGORIES_FACET } from "src/utils/algolia";
import { queryStringKeyMap } from "../use-handle-search-params";
import { TreeType } from "src/types/Tree";

type UseCategoryTreeProps = {
  gender: string;
  brandName?: string;
  categoryPageId: string;
  pageContentType: PLPType;
};

function useCategoryTree({
  gender,
  brandName,
  categoryPageId,
  pageContentType,
}: UseCategoryTreeProps): {
  isLoading: boolean;
  tree: TreeType<CategoryNodeType>;
  parentIds: string[];
  rootIds: string[];
} {
  const isTreeSet = useRef(false);
  const itemsLengthRef = useRef(0);
  const [searchParams, setSearchParams] = useState(() => {
    const url = new URL(location.toString());

    return url.searchParams.getAll(queryStringKeyMap[CATEGORIES_FACET]) || [];
  });

  const {
    initialRefine,
    refine,
    items,
    categoryPageId: currentCategoryPageId,
    tree,
    parentIds,
    rootIds,
    isLoading,
  } = useCategoryTreeContext();
  const treeDispatch = useCategoryTreeDispatch();

  const updateSearchParams = useCallback(() => {
    const url = new URL(location.toString());

    const newParams =
      url.searchParams.getAll(queryStringKeyMap[CATEGORIES_FACET]) || [];

    setSearchParams(newParams);
  }, []);

  useEffect(() => {
    window.addEventListener("popstate", updateSearchParams);

    return () => {
      window.removeEventListener("popstate", updateSearchParams);
    };
  }, [updateSearchParams]);

  // Pre select filters
  useEffect(() => {
    // Get Catgory Page Ids and exclude any parent category
    const selectedItems = tree.filter((category) =>
      searchParams?.includes(category.slug)
    );

    const selectedCatPageIds = selectedItems.flatMap((category) => {
      if (
        !selectedItems.some(
          (subCategory) => subCategory.parentId === category.itemId
        )
      ) {
        return [category.categoryPageId];
      }

      return [];
    });

    initialRefine(selectedCatPageIds);
  }, [tree, searchParams, categoryPageId, initialRefine, treeDispatch]);

  useEffect(() => {
    if (currentCategoryPageId !== categoryPageId) {
      isTreeSet.current = false;
    }
  }, [currentCategoryPageId, categoryPageId, treeDispatch]);

  useEffect(() => {
    if (itemsLengthRef.current !== items.length && isTreeSet.current) {
      isTreeSet.current = false;
    }

    if (isTreeSet.current) {
      return;
    }

    const filteredItems = items.filter((cat: string) => {
      const splitVal = cat.split("||") || [];

      return splitVal.at(4) === localeFix({ gender });
    });

    const generatedTree = generateTree<
      (typeof filteredItems)[number],
      CategoryNodeType
    >({
      items: filteredItems,
      brandName,
      transform: transformCategoryValue,
    });

    if (generatedTree.length === 0) {
      return;
    }

    treeDispatch({
      type: "SET_INITIAL_STATE",
      payload: {
        tree: generatedTree,
        categoryPageId,
        isLoading: false,
        canFilterCategoryPageId: pageContentType === PLPType.CATEGORY_PLP,
      },
    });

    isTreeSet.current = true;
    itemsLengthRef.current = items.length;
  }, [
    items,
    gender,
    brandName,
    categoryPageId,
    pageContentType,
    isLoading,
    treeDispatch,
    refine,
  ]);

  return { tree, parentIds, rootIds, isLoading };
}

export { useCategoryTree };
